import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { UI, useLang } from "../../lib/lang";

export const Field = ({ id, label, required, error, hint, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="mb-2 block text-sm font-semibold text-[color:var(--navy)]">
      {label} {required ? <span className="text-[#B91C1C]" aria-hidden="true">*</span> : <span className="font-normal text-slate-500">(optional)</span>}
    </label>
    {children}
    {hint && !error && <p id={`${id}-hint`} className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-[#B91C1C]" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const TextInput = ({ id, value, onChange, error, required, type = "text", ...rest }) => (
  <input
    id={id} name={id} type={type} value={value} onChange={(e) => onChange(id, e.target.value)}
    aria-invalid={!!error} aria-required={required} aria-describedby={error ? `${id}-error` : undefined}
    className="field-input" data-testid={`input-${id}`} {...rest}
  />
);

export const TextArea = ({ id, value, onChange, error, ...rest }) => (
  <textarea
    id={id} name={id} rows={5} value={value} onChange={(e) => onChange(id, e.target.value)}
    aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
    className="field-input resize-y" data-testid={`input-${id}`} {...rest}
  />
);

export const SelectInput = ({ id, value, onChange, error, options, placeholder = "Bitte wählen" }) => (
  <select
    id={id} name={id} value={value} onChange={(e) => onChange(id, e.target.value)}
    aria-invalid={!!error} className="field-input" data-testid={`input-${id}`}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const CheckField = ({ id, checked, onChange, error, children, required }) => (
  <div>
    <label htmlFor={id} className="flex cursor-pointer gap-3 text-sm leading-relaxed text-slate-700">
      <input
        id={id} name={id} type="checkbox" checked={checked} onChange={(e) => onChange(id, e.target.checked)}
        aria-invalid={!!error} aria-required={required} data-testid={`checkbox-${id}`}
        className="mt-1 h-5 w-5 shrink-0 accent-[#1B2F5B]"
      />
      <span>{children}{required && <span className="text-[#B91C1C]" aria-hidden="true"> *</span>}</span>
    </label>
    {error && <p role="alert" className="mt-1.5 pl-8 text-sm text-[#B91C1C]" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
    <label htmlFor="website">{UI[useLang()].honeypot}</label>
    <input id="website" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange("website", e.target.value)} />
  </div>
);

export const PrivacyAck = ({ checked, onChange, error }) => {
  const t = UI[useLang()];
  return (
  <CheckField id="privacy_ack" checked={checked} onChange={onChange} error={error} required>
    {t.privacyPre}<Link to="/datenschutz" target="_blank" className="font-semibold text-[color:var(--teal-ink)] underline">{t.privacyLink}</Link>{t.privacyPost}
  </CheckField>
  );
};

export const FormAlert = ({ type, children }) => (
  <div role={type === "error" ? "alert" : "status"} data-testid={`form-alert-${type}`}
    className={`flex gap-3 rounded-2xl p-4 text-sm ${type === "error" ? "bg-red-50 text-[#991B1B]" : "bg-emerald-50 text-emerald-900"}`}>
    {type === "error" ? <AlertCircle size={18} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={18} className="mt-0.5 shrink-0" />}
    <div>{children}</div>
  </div>
);

export const SuccessPanel = ({ title, reference, children, onReset, testid }) => {
  const ref = useRef(null);
  const t = UI[useLang()];
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 110, behavior: "smooth" });
    el.focus({ preventScroll: true });
  }, []);
  return (
  <div ref={ref} tabIndex={-1} className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-8 md:p-12 outline-none" role="status" data-testid={testid}>
    <CheckCircle2 size={40} className="text-emerald-700" />
    <h2 className="mt-5 text-2xl md:text-3xl font-bold text-[color:var(--navy)]">{title}</h2>
    <p className="mt-3 text-slate-700">{t.reference} <strong data-testid="success-reference">{reference}</strong></p>
    <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">{children}</div>
    <button type="button" onClick={onReset} className="mt-8 rounded-full border border-[color:var(--navy)] px-6 py-3 text-sm font-semibold text-[color:var(--navy)] hover:bg-white" data-testid="success-reset-button">
      {t.reset}
    </button>
  </div>
  );
};
